import { useEffect, useState } from 'react';
import type { LibraryItem } from '../lib/api';
import { requestDownload } from '../lib/api';

type Props = {
  item: LibraryItem | null;
  onClose: () => void;
};

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as
  | string
  | undefined;

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: { sitekey: string; callback: (t: string) => void; theme?: string }
      ) => string;
      reset: (id?: string) => void;
    };
    onloadTurnstileCallback?: () => void;
  }
}

const projectTypes = [
  'Workflow automation',
  'AI agent / assistant',
  'Document processing',
  'CRM / data integration',
  'Internal tooling',
  'Other',
];

export default function DownloadFormModal({ item, onClose }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [projectType, setProjectType] = useState(projectTypes[0]);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [readyFiles, setReadyFiles] = useState<{ downloadUrl: string; filename: string }[]>([]);

  useEffect(() => {
    if (!item) return;
    if (!TURNSTILE_SITE_KEY) return;
    if (document.querySelector('script[data-turnstile]')) return;
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.defer = true;
    s.setAttribute('data-turnstile', 'true');
    document.body.appendChild(s);
  }, [item]);

  useEffect(() => {
    if (!item) {
      setName('');
      setEmail('');
      setPhone('');
      setProjectType(projectTypes[0]);
      setTurnstileToken('');
      setError(null);
      setDone(false);
      setReadyFiles([]);
    }
  }, [item]);

  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [item, onClose]);

  if (!item) return null;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const response = await requestDownload({
        name,
        email,
        phone,
        projectType,
        blueprintKey: item.key,
        attachmentKeys: item.attachments.map((a) => a.key),
        turnstileToken,
      });
      const allFiles = [
        { downloadUrl: response.downloadUrl, filename: response.filename },
        ...response.attachments,
      ];
      if (allFiles.length === 1) {
        const a = document.createElement('a');
        a.href = response.downloadUrl;
        a.download = response.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        setReadyFiles(allFiles);
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="download-modal-title"
    >
      <div
        className="grain relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-4xl border border-white/10 bg-charcoal p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-5 top-5 font-sans text-xs uppercase tracking-label text-stone hover:text-white"
        >
          Close ✕
        </button>

        {!done ? (
          <>
            <p className="label">Library Download</p>
            <h3
              id="download-modal-title"
              className="mt-3 font-display text-2xl font-light text-white"
            >
              {item.title}
            </h3>
            <p className="mt-2 font-sans text-caption text-stone">
              Tell us where to send updates and we'll start your download.
            </p>

            <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
              <Field
                label="Name"
                value={name}
                onChange={setName}
                required
                autoComplete="name"
              />
              <Field
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                required
                autoComplete="email"
              />
              <Field
                label="Phone"
                type="tel"
                value={phone}
                onChange={setPhone}
                required
                autoComplete="tel"
              />
              <div>
                <label className="label mb-2 block">Project Type</label>
                <select
                  value={projectType}
                  onChange={(e) => setProjectType(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-black/60 px-4 py-3 font-sans text-sm text-white focus:border-gold focus:outline-none"
                >
                  {projectTypes.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              {TURNSTILE_SITE_KEY && (
                <div
                  className="cf-turnstile"
                  data-sitekey={TURNSTILE_SITE_KEY}
                  data-theme="dark"
                  data-callback="onloadTurnstileCallback"
                  ref={(el) => {
                    if (!el) return;
                    if (!window.turnstile) {
                      window.onloadTurnstileCallback = () => {
                        window.turnstile?.render(el, {
                          sitekey: TURNSTILE_SITE_KEY,
                          theme: 'dark',
                          callback: (t) => setTurnstileToken(t),
                        });
                      };
                    } else if (!el.dataset.rendered) {
                      el.dataset.rendered = 'true';
                      window.turnstile.render(el, {
                        sitekey: TURNSTILE_SITE_KEY,
                        theme: 'dark',
                        callback: (t) => setTurnstileToken(t),
                      });
                    }
                  }}
                />
              )}

              {error && (
                <p
                  role="alert"
                  aria-live="polite"
                  className="font-sans text-caption text-gold"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-gold bg-gold px-7 py-3.5 font-sans text-[0.7rem] uppercase tracking-label text-black transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {submitting ? 'Sending…' : 'Download Blueprint'} →
              </button>
            </form>
          </>
        ) : readyFiles.length > 0 ? (
          <div className="py-6 text-center" role="status" aria-live="polite">
            <p className="label mb-3">Ready</p>
            <h3 className="font-display text-3xl font-light text-white">
              Your files are ready.
            </h3>
            <p className="mt-3 font-sans text-caption text-white/80">
              Click each file below to download. We've recorded your request — Yousif will follow up shortly.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              {readyFiles.map(({ downloadUrl, filename }) => {
                const ext = filename.split('.').pop()?.toUpperCase() ?? 'FILE';
                return (
                  <a
                    key={filename}
                    href={downloadUrl}
                    download={filename}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-gold bg-gold px-7 py-3.5 font-sans text-[0.7rem] uppercase tracking-label text-black transition-opacity hover:opacity-90"
                  >
                    ↓ Download {ext}
                  </a>
                );
              })}
            </div>
            <button
              onClick={onClose}
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-2.5 font-sans text-[0.7rem] uppercase tracking-label text-white/90 hover:text-white"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="py-6 text-center" role="status" aria-live="polite">
            <p className="label mb-3">Sent</p>
            <h3 className="font-display text-3xl font-light text-white">
              Your download is starting.
            </h3>
            <p className="mt-4 font-sans text-body text-white/85">
              If it doesn't begin, check your browser's blocked downloads. We've
              recorded your request — Yousif will follow up shortly.
            </p>
            <button
              onClick={onClose}
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-2.5 font-sans text-[0.7rem] uppercase tracking-label text-white/90 hover:text-white"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  required,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  autoComplete?: string;
}) {
  return (
    <div>
      <label className="label mb-2 block">{label}</label>
      <input
        type={type}
        value={value}
        required={required}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl border border-white/10 bg-black/60 px-4 py-3 font-sans text-sm text-white placeholder-stone focus:border-gold focus:outline-none"
      />
    </div>
  );
}
